import {
  useManualKpis,
  useKpiSuggestions,
  ManualKpiResolution,
  ManualKpiToUpdate,
  generateKpi,
  generateKpisToUpdate
} from '@domain/shifthandover'
import {ExpandedGroupIds} from '@hconnect/common/hooks/useExpandGroups'
import {ProcessStage} from '@hconnect/common/types'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {ManualKpiPreview} from '@widgets/kpis'
import {AxiosError} from 'axios'
import {isEmpty} from 'lodash'
import React, {FC, useEffect} from 'react'
import {Control} from 'react-hook-form'

import {ManualKpiEdit} from '../components/kpis'
import {KpiFormData} from '../types'

type PerformanceManualKpiContainerProps = {
  isEditMode: boolean
  isEnabled: boolean
  openProcessStages: string[]
  setOpenProcessStages: (id: ExpandedGroupIds) => void
  processStages: ProcessStage[]
  isProcessStagesLoading: boolean
  manualKpiList: ManualKpiToUpdate[]
  setManualKpiList: (manualKpiList: ManualKpiToUpdate[]) => void
  control: Control<KpiFormData>
  updateError: AxiosError | null
  updateReset: () => void
  refetchProcessStages?: () => void
  isSingleShift: boolean
  timeRange: {endDate: string}
}

export const PerformanceManualKpiContainer: FC<PerformanceManualKpiContainerProps> = ({
  isEditMode,
  isEnabled,
  openProcessStages,
  setOpenProcessStages,
  processStages,
  isProcessStagesLoading,
  manualKpiList,
  setManualKpiList,
  control,
  updateError,
  updateReset,
  refetchProcessStages,
  isSingleShift,
  timeRange
}) => {
  const {plantId} = useUpmPlantContext()
  const timezone = useUpmPlantTimezone()
  const {
    data: kpis,
    isLoading: isKpisLoading,
    isSuccess: isKpisSuccess
  } = useManualKpis({
    plantId,
    resolution: isSingleShift ? ManualKpiResolution.PerShift : ManualKpiResolution.Daily,
    timestamp: timeRange.endDate,
    timezone,
    options: {
      enabled: isEnabled
    }
  })

  const {data: kpiSuggestions, isSuccess: isKpiSuggestionsSuccess} = useKpiSuggestions({
    plantId,
    options: {
      enabled: !isKpisLoading && isEmpty(kpis) && isEnabled
    }
  })

  useEffect(() => {
    if (isKpisSuccess && !isEmpty(kpis)) {
      setManualKpiList(generateKpisToUpdate(kpis))
    }
  }, [isKpisSuccess, kpis, setManualKpiList])

  useEffect(() => {
    if (isKpiSuggestionsSuccess && !isKpisLoading && isEmpty(kpis) && kpiSuggestions) {
      const kpiSuggestionsGenerated = kpiSuggestions.map((kpi) => generateKpi(kpi))
      setManualKpiList(kpiSuggestionsGenerated)
    }
  }, [isKpiSuggestionsSuccess, isKpisLoading, kpis, kpiSuggestions, setManualKpiList])

  return isEditMode ? (
    <ManualKpiEdit
      setManualKpiList={setManualKpiList}
      processStages={processStages}
      manualKpiList={manualKpiList}
      isProcessStagesLoading={isProcessStagesLoading}
      openProcessStages={openProcessStages}
      setOpenProcessStages={setOpenProcessStages}
      control={control}
      updateError={updateError}
      updateReset={updateReset}
      refetchProcessStages={refetchProcessStages}
    />
  ) : (
    <ManualKpiPreview
      manualKpiList={manualKpiList}
      isProcessStagesLoading={isProcessStagesLoading}
      openProcessStages={openProcessStages}
      setOpenProcessStages={setOpenProcessStages}
      processStages={processStages}
    />
  )
}
